import React, { useState, useEffect } from "react";
import { Modal, Button, ListGroup, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useI18n } from "../i18n";
import {
  formatDateShort,
  formatTime,
  formatCurrency,
} from "../utils/formatters";

interface Flight {
  flight_number: string;
  price: number;
  original_price?: number;
  original_currency?: string;
  date: string;
  departure_time: string;
  arrival_time: string;
  duration: string;
  flight_key: string;
}

interface FlightSelectionModalProps {
  show: boolean;
  onHide: () => void;
  trackerId: number;
  flightType: "outbound" | "return";
  currentFlight?: Flight;
  onFlightSelected: () => void;
  trackerDepartureDate?: string;
  trackerReturnDate?: string;
  // Optional custom API functions for admin use
  customGetAvailableFlights?: (trackerId: number, flightType: string) => Promise<Flight[]>;
  customSelectFlight?: (trackerId: number, flightSelection: any) => Promise<void>;
}

const FlightSelectionModal: React.FC<FlightSelectionModalProps> = ({
  show,
  onHide,
  trackerId,
  flightType,
  currentFlight,
  onFlightSelected,
  trackerDepartureDate,
  trackerReturnDate,
  customGetAvailableFlights,
  customSelectFlight,
}) => {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFlightKey, setSelectedFlightKey] = useState(
    currentFlight?.flight_key || "",
  );
  const { t } = useI18n();

  const loadAvailableFlights = async () => {
    setLoading(true);
    setError("");
    try {
      let data;
      if (customGetAvailableFlights) {
        data = await customGetAvailableFlights(trackerId, flightType);
      } else {
        const response = await axios.get(
          `/api/trackers/${trackerId}/available-flights`,
          {
            params: { flight_type: flightType },
          },
        );
        data = response.data;
      }
      setFlights(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("flightSelection.loadingFlights"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadAvailableFlights();
      setSelectedFlightKey(currentFlight?.flight_key || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, trackerId, flightType]);

  const handleSelectFlight = async () => {
    if (!selectedFlightKey) {
      setError(t("flightSelection.selectFlight"));
      return;
    }

    const selectedFlight = flights.find(
      (f) => f.flight_key === selectedFlightKey,
    );
    if (!selectedFlight) {
      setError(t("flightSelection.flightNotFound"));
      return;
    }

    // Validate date range (allow same-day round trips)
    if (flightType === "outbound" && trackerReturnDate) {
      // Get the return date that will be in the tracker
      const currentReturnDate = trackerReturnDate;
      if (
        new Date(selectedFlight.date).setHours(0, 0, 0, 0) >
        new Date(currentReturnDate).setHours(0, 0, 0, 0)
      ) {
        setError(t("flightSelection.outboundDateAfterReturn"));
        return;
      }
    } else if (flightType === "return" && trackerDepartureDate) {
      // For return flights, we need to check against the current or soon-to-be departure date
      if (
        new Date(selectedFlight.date).setHours(0, 0, 0, 0) <
        new Date(trackerDepartureDate).setHours(0, 0, 0, 0)
      ) {
        setError(t("flightSelection.returnDateBeforeDeparture"));
        return;
      }
    }

    setLoading(true);
    setError("");
    try {
      const payload =
        flightType === "outbound"
          ? {
              outbound_key: selectedFlightKey,
              outbound_date: selectedFlight.date,
            }
          : { return_key: selectedFlightKey, return_date: selectedFlight.date };

      if (customSelectFlight) {
        await customSelectFlight(trackerId, payload);
      } else {
        await axios.put(`/api/trackers/${trackerId}/select-flight`, payload);
      }
      
      toast.success(t("flightSelection.updateSuccess"));
      onFlightSelected();
      onHide();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t("flightSelection.updateError");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const title =
    flightType === "outbound"
      ? t("flightSelection.titleOutbound")
      : t("flightSelection.titleReturn");

  // Group flights by date
  const flightsByDate = flights.reduce(
    (acc, flight) => {
      if (!acc[flight.date]) {
        acc[flight.date] = [];
      }
      acc[flight.date].push(flight);
      return acc;
    },
    {} as Record<string, Flight[]>,
  );

  const sortedDates = Object.keys(flightsByDate).sort();

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-airplane"></i> {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {trackerDepartureDate && (
          <Alert variant="info" className="mb-3">
            <i className="bi bi-info-circle"></i> {t("flightSelection.currentTrackerDate")} {" "}
            <strong>
              {flightType === "outbound"
                ? formatDateShort(trackerDepartureDate)
                : formatDateShort(trackerReturnDate || "")}
            </strong>
            {flightType === "outbound" && trackerReturnDate && (
              <span> ({t("flightSelection.returnLabel")}: {formatDateShort(trackerReturnDate)})</span>
            )}
            {flightType === "return" && trackerDepartureDate && (
              <span> ({t("flightSelection.outboundLabel")}: {formatDateShort(trackerDepartureDate)})</span>
            )}
          </Alert>
        )}

            {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">{t("flightSelection.loadingFlights")}</p>
          </div>
        ) : flights.length === 0 ? (
          <Alert variant="info">{t("flightSelection.noFlightsAvailable")}</Alert>
        ) : (
          <div>
            {sortedDates.map((date) => (
              <div key={date} className="mb-3">
                <div
                  className="p-2 mb-2"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderLeft: "4px solid #0d6efd",
                    fontWeight: "bold",
                  }}
                >
                  <i className="bi bi-calendar-day"></i> {formatDateShort(date)}
                      {date !== trackerDepartureDate &&
                    flightType === "outbound" && (
                      <span className="badge bg-warning text-dark ms-2">
                        {t("flightSelection.dateDifferent") || "Different date"}
                      </span>
                    )}
                  {date !== trackerReturnDate && flightType === "return" && (
                    <span className="badge bg-warning text-dark ms-2">
                      {t("flightSelection.dateDifferent") || "Different date"}
                    </span>
                  )}
                </div>
                <ListGroup>
                  {flightsByDate[date].map((flight) => (
                    <ListGroup.Item
                      key={flight.flight_key}
                      action
                      active={selectedFlightKey === flight.flight_key}
                      onClick={() => setSelectedFlightKey(flight.flight_key)}
                      className="cursor-pointer"
                      style={
                        selectedFlightKey === flight.flight_key
                          ? {
                              backgroundColor: "#0d6efd",
                              borderColor: "#0d6efd",
                            }
                          : {}
                      }
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6
                            className="mb-1"
                            style={
                              selectedFlightKey === flight.flight_key
                                ? { color: "white" }
                                : {}
                            }
                          >
                            <i className="bi bi-airplane-fill"></i>{" "}
                            {flight.flight_number}
                          </h6>
                          <small
                            style={
                              selectedFlightKey === flight.flight_key
                                ? { color: "rgba(255, 255, 255, 0.9)" }
                                : {}
                            }
                          >
                            <i className="bi bi-calendar3"></i>{" "}
                            {formatDateShort(flight.date)}
                            {" • "}
                            <i className="bi bi-clock"></i>{" "}
                            {formatTime(flight.departure_time)} -{" "}
                            {formatTime(flight.arrival_time)}
                            {flight.duration && ` (${flight.duration})`}
                          </small>
                        </div>
                        <div className="text-end">
                          <h5
                            className="mb-0"
                            style={
                              selectedFlightKey === flight.flight_key
                                ? { color: "white", fontWeight: "bold" }
                                : { color: "#0d6efd" }
                            }
                          >
                            {formatCurrency(flight.price, flight.original_price, flight.original_currency)}
                          </h5>
                          {selectedFlightKey === flight.flight_key && (
                            <small style={{ color: "#90ee90" }}>
                              <i className="bi bi-check-circle-fill"></i>{" "}
                              {t("flightSelection.selected")}
                            </small>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("flightSelection.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSelectFlight}
          disabled={loading || !selectedFlightKey}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                className="me-2"
              />
              {t("flightSelection.updating")}
            </>
          ) : (
            <>
              <i className="bi bi-check-circle"></i> {t("flightSelection.confirmSelection")}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FlightSelectionModal;
